<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useQuasar } from 'quasar';

import type { ContextDto, Locales } from '@/services/types/api.types';
import { useContextsStore } from '@/stores';

import ContextEditDialog from './ContextEditDialog.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import { useI18n } from 'vue-i18n';
import { getTranslation } from '@/composables/data-display-utils';

const $q = useQuasar();
const contextsStore = useContextsStore();

const { t } = useI18n();
const props = defineProps<{
  parent: number;
  parentId: number;
  languageEnabled: Locales[];
}>();

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ContextDto) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'type',
    label: t('global.label.type'),
    field: (row: ContextDto) => contextTypes.value.find((x) => x.value === row.type)?.label,
    align: 'left',
  },
]);

const contexts = computed(() => contextsStore.getOrderedContexts(props.parent, props.parentId));
const contextTypes = computed(() => contextsStore.getTypes());

onMounted(async () => {
  await contextsStore.fetchContextsByParent(props.parent, props.parentId);
});

const handleAddRow = () => {
  const context: ContextDto = contextsStore.createContext(props.parent, props.parentId);
  $q.dialog({
    component: ContextEditDialog,
    componentProps: {
      mode: 'add',
      context,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleDeleteRow = (row: ContextDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('context.name') }),
    message: t('global.dialogs.deleteMessage', { type: t('context.name') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await contextsStore.deleteContext(row);
  });
};

const handleRowClick = (row: ContextDto) => {
  $q.dialog({
    component: ContextEditDialog,
    componentProps: {
      mode: 'edit',
      context: row,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleReorder = (source: ContextDto, target: ContextDto) => {
  contextsStore.saveContext({ ...contextsStore.createContextData(source), version: 'v1' });
  contextsStore.saveContext({ ...contextsStore.createContextData(target), version: 'v1' });
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="contexts"
      :columns="columns"
      :title="t('context.title')"
      itemKey="id"
      @add-row="handleAddRow"
      @row-click="handleRowClick"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    />
  </div>
</template>
